import { hasDebugSwitch } from '@web3/utils';
import { isBrowser } from './trick';

export type ScTrackFn = (event: string, ops?: Record<string, string | number | boolean>) => void;

export function strEnum<T extends string>(o: Array<T>): { [K in T]: K } {
  return o.reduce((res, key) => {
    res[key] = key;
    return res;
  }, Object.create(null));
}

export const isSopolia = (chain: string | number) => {
  return ['sepolia', 11155111, '11155111'].includes(chain);
};

export const sopoliaRpcUrl = 'https://eth-sepolia.g.alchemy.com/v2/EaKwqSJApyUIsuALlx7iizFoKyGlCaDH';

export const getRpcUrl = (chainCode: string) => {
  return `https://api-node.bybit.com/spot/api/web3/node/${chainCode.toLowerCase()}`;
};

export const dependencyContainer = () => {
  let _deps: unknown[] | undefined;
  const set = (deps: unknown[]) => {
    _deps = deps;
  };
  const isChanged = (deps: unknown[]) => {
    if (!deps || !_deps || deps.length !== _deps.length) {
      return true;
    }

    return _deps.some((dep, idx) => dep !== deps[idx]);
  };
  const checkAndRun = <T>(fn: () => T, deps: unknown[], force = false) => {
    let res: T;
    if (isChanged(deps) || force) {
      const fnRes: any = fn();
      res = fnRes;
      set(deps);
      if (fnRes.catch && fnRes.then) {
        fnRes.catch((err) => {
          _deps = undefined;
        });
      }
      return {
        result: res,
        isChanged: true,
      };
    }
    return {
      result: res!,
      isChanged: false,
    };
  };

  return {
    set,
    isChanged,
    checkAndRun,
  };
};

export const cleanLastRun = <T extends (...args: any[]) => () => void>(fn: T) => {
  let cleaner: undefined | (() => void);
  return (...args: Parameters<T>) => {
    if (typeof cleaner === 'function') {
      cleaner();
      cleaner = undefined;
    }
    cleaner = fn(...args);
  };
};

export const addHexPrefix = (a: string) => {
  return a.startsWith('0x') ? a : `0x${a}`;
};

export const getCache = (() => {
  const map = new Map<string, any>();

  return async (key: string, fn: () => any) => {
    if (map.has(key)) {
      return map.get(key);
    }
    map.set(
      key,
      Promise.resolve(fn()).catch((err) => {
        map.delete(key);
        throw err;
      }),
    );
    return map.get(key);
  };
})();

let isWeb3Debugging = hasDebugSwitch('web3-infra-debugging');

export const enableDebugging = () => {
  isWeb3Debugging = true;
  if (isBrowser) {
    window.localStorage.setItem('web3-infra-debugging', 'true');
  }
};

export const debugLog = (...args: any[]) => {
  if (isWeb3Debugging) {
    console.trace('[web3infra]:', ...args);
  }
};
